import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalBar as TypeIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import BackButton from '../../components/common/BackButton';

// Importar el servicio real de Firebase
import { firebaseTypesService } from '../../services/firebaseService';

const AdminTypes = () => {
  const [open, setOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: 'default',
    description: ''
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null });

  const queryClient = useQueryClient();

  // Query para obtener tipos usando Firebase
  const { data: types = [], isLoading, error } = useQuery({
    queryKey: ['firebaseTypes'],
    queryFn: async () => {
      try {
        console.log('🍾 AdminTypes: Consultando tipos desde Firebase...');
        const response = await firebaseTypesService.getAll();
        console.log('✅ Tipos obtenidos de Firebase:', response.length);
        return response;
      } catch (error) {
        console.error('❌ Error obteniendo tipos de Firebase:', error);
        throw error;
      }
    }
  });

  // Mutation para crear tipo usando Firebase
  const createMutation = useMutation({
    mutationFn: (formData) => firebaseTypesService.create(formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseTypes']);
      setOpen(false);
      resetForm();
      toast.success('Tipo creado exitosamente');
    },
    onError: (error) => {
      console.error('❌ Error al crear tipo:', error);
      toast.error('Error al crear el tipo');
    }
  });

  // Mutation para actualizar tipo usando Firebase
  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => firebaseTypesService.update(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseTypes']);
      setOpen(false);
      resetForm();
      toast.success('Tipo actualizado exitosamente');
    },
    onError: (error) => {
      console.error('❌ Error al actualizar tipo:', error);
      toast.error('Error al actualizar el tipo');
    }
  });

  // Mutation para eliminar tipo usando Firebase
  const deleteMutation = useMutation({
    mutationFn: firebaseTypesService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseTypes']);
      setDeleteDialog({ open: false, type: null });
      toast.success('Tipo eliminado exitosamente');
    },
    onError: (error) => {
      console.error('❌ Error al eliminar tipo:', error);
      toast.error('Error al eliminar el tipo');
      setDeleteDialog({ open: false, type: null });
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      color: 'default',
      description: ''
    });
    setEditingType(null);
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name,
        color: type.color,
        description: type.description
      });
    } else {
      resetForm();
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    if (editingType) {
      updateMutation.mutate({
        id: editingType.id,
        formData
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDeleteClick = (type) => {
    setDeleteDialog({ open: true, type });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.type) {
      deleteMutation.mutate(deleteDialog.type.id);
    }
  };

  const colorOptions = [
    { value: 'default', label: 'Por defecto' },
    { value: 'primary', label: 'Primario' },
    { value: 'secondary', label: 'Secundario' },
    { value: 'error', label: 'Rojo' },
    { value: 'warning', label: 'Naranja' },
    { value: 'info', label: 'Azul' },
    { value: 'success', label: 'Verde' }
  ];

  if (isLoading) return <LoadingSpinner />;

  return (
    <Box>
      <BackButton />
      
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypeIcon />
          Gestión de Tipos de Vino
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Tipo
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los tipos
        </Alert>
      )}

      {/* Tabla de tipos */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Color</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((type) => (
              <TableRow key={type.id}>
                <TableCell>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {type.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={type.name}
                    color={type.color}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {type.description}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton
                      onClick={() => handleOpenDialog(type)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton
                      onClick={() => handleDeleteClick(type)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {types.length === 0 && !isLoading && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            No hay tipos registrados
          </Typography>
        </Box>
      )}

      {/* Dialog para crear/editar tipo */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingType ? 'Editar Tipo' : 'Nuevo Tipo'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                autoFocus
                label="Nombre del Tipo"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />

              <TextField
                select
                label="Color del Chip"
                fullWidth
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                SelectProps={{
                  native: true,
                }}
              >
                {colorOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>

              <TextField
                label="Descripción"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />

              {formData.name && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Vista previa:
                  </Typography>
                  <Chip 
                    label={formData.name}
                    color={formData.color}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              color="secondary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingType ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, type: null })}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar el tipo "{deleteDialog.type?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, type: null })}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTypes; 